/** Investors — composed screen (mandate §14, DEC-065). The package ships this route as a stub whose copy
 *  invites the build; the server has owned the flow since the manage family landed (grant → one-time link →
 *  portal; revoke; terms; payments) and no UI ever called it. Hand-written in the canonical design language,
 *  reading the investors model slice; its visual baseline is generated, not compared against the reference.
 *
 *  The one security-shaped rule this screen keeps: the portal link token exists in plaintext exactly once —
 *  in the grant response, shown in the issued box below with a copy control. It is never listed again,
 *  because the server keeps only the hash.
 */
import { useEffect } from 'react';
import type { ViewModel } from '@/screens/generated/view-model';
import type { CSSProperties } from 'react';

const card: CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--card-line)',
  boxShadow: 'var(--shadow-card)',
  borderRadius: '16px',
  padding: '16px 18px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  minWidth: 0,
};
const label: CSSProperties = {
  fontSize: '9.5px',
  letterSpacing: '0.13em',
  textTransform: 'uppercase',
  color: 'var(--muted2)',
  fontWeight: 600,
};
const field: CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: '10px',
  border: '1px solid var(--l10)',
  background: 'var(--l2)',
  color: 'var(--text)',
  fontSize: '12px',
};
const fieldLabel: CSSProperties = {
  display: 'block',
  fontSize: '9.5px',
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'var(--muted)',
  margin: '0 0 5px',
};
const ghostBtn: CSSProperties = {
  fontSize: '11.5px',
  padding: '6px 13px',
  borderRadius: '99px',
  border: '1px solid var(--l10)',
  background: 'transparent',
  color: 'var(--text-mid)',
  cursor: 'pointer',
};
const primaryBtn: CSSProperties = {
  fontSize: '11.5px',
  fontWeight: 600,
  padding: '7px 15px',
  borderRadius: '99px',
  border: '1px solid color-mix(in srgb, var(--slate-fill) 50%, transparent)',
  background: 'color-mix(in srgb, var(--slate-fill) 10%, transparent)',
  color: 'var(--slate)',
  cursor: 'pointer',
};
const dangerBtn: CSSProperties = {
  fontSize: '11px',
  padding: '5px 11px',
  borderRadius: '99px',
  border: '1px solid color-mix(in srgb, var(--neg) 45%, transparent)',
  background: 'transparent',
  color: 'var(--neg)',
  cursor: 'pointer',
};
const rowCard: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '11px',
  padding: '10px 12px',
  borderRadius: '11px',
  border: '1px solid var(--l7)',
  background: 'var(--l2)',
};

export function InvestorsView({ m }: { m: ViewModel }) {
  const I = m.invState;
  // The list is server truth and mutation-shaped, so it is fetched on entry rather than carried in the
  // composed dataset. One call; refreshes ride the mutations.
  useEffect(() => {
    if (!I) m.invLoad?.();
  }, [I, m]);

  return (
    <div style={{ padding: '24px 12px 40px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ minWidth: 0 }}>
        <div style={label}>Manage · Investors</div>
        <div style={{ fontSize: '15px', fontWeight: 500, letterSpacing: '-0.01em', marginTop: '7px' }}>
          Viewer grants, terms and payments
        </div>
        <div style={{ fontSize: '11.5px', color: 'var(--muted)', marginTop: '4px', maxWidth: '70ch' }}>
          A grant gives one investor read access through the portal. The link is minted once — copy it when it
          is shown, because only its hash survives. Revoking stops the portal answering immediately.
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,3fr) minmax(0,2fr)', gap: '12px' }}>
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={label}>Grants</div>
            <div style={{ flex: 1 }} />
            <button style={ghostBtn} onClick={m.invLoad} aria-label="Refresh investor grants">
              Refresh
            </button>
          </div>

          {I?.loading ? (
            <div style={{ fontSize: '11.5px', color: 'var(--muted)' }}>Loading the server’s list…</div>
          ) : null}
          {I?.error ? (
            <div style={{ fontSize: '11.5px', color: 'var(--neg)' }}>
              {I.error}{' '}
              <button style={{ ...ghostBtn, fontSize: '10.5px' }} onClick={m.invLoad}>
                Retry
              </button>
            </div>
          ) : null}
          {I && !I.loading && !I.error && (m.invRows ?? []).length === 0 ? (
            <div style={{ fontSize: '11.5px', color: 'var(--muted)' }}>
              No grants yet — the form on the right creates the first one.
            </div>
          ) : null}

          {(m.invRows ?? []).map((g) => (
            <div key={g.id} style={rowCard}>
              <div
                style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '99px',
                  border: '1px solid var(--l10)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10.5px',
                  fontWeight: 600,
                  color: 'var(--text-mid)',
                  flex: 'none',
                }}
              >
                {g.initials}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '12px', fontWeight: 600 }}>{g.name}</div>
                <div style={{ fontSize: '10px', color: 'var(--muted)', marginTop: '2px' }}>
                  Granted {g.created} · {g.expires}
                </div>
              </div>
              <span
                style={{
                  fontSize: '10px',
                  padding: '3px 10px',
                  borderRadius: '99px',
                  border: '1px solid var(--l10)',
                  color: g.statusFg,
                  textTransform: 'capitalize',
                }}
              >
                {g.status}
              </span>
              <button
                style={ghostBtn}
                onClick={() => m.invTermsOpen?.(g.id, g.name)}
                aria-label={`Terms for ${g.name}`}
              >
                Terms
              </button>
              {g.revocable ? (
                I?.revokeConfirm?.id === g.id ? (
                  <>
                    <span style={{ fontSize: '10.5px', color: 'var(--neg)' }}>Revoke?</span>
                    <button style={ghostBtn} onClick={m.invRevokeCancel} disabled={I.busy}>
                      Keep
                    </button>
                    <button
                      style={dangerBtn}
                      onClick={() => void m.invRevokeGo?.(g.id, g.name)}
                      disabled={I.busy}
                      aria-label={`Confirm revoke ${g.name}`}
                    >
                      {I.busy ? 'Revoking…' : 'Revoke'}
                    </button>
                  </>
                ) : (
                  <button
                    style={dangerBtn}
                    onClick={() => m.invRevokeAsk?.(g.id, g.name)}
                    aria-label={`Revoke ${g.name}`}
                  >
                    Revoke
                  </button>
                )
              ) : null}
            </div>
          ))}

          {I?.terms ? (
            <div
              style={{
                border: '1px solid var(--l8)',
                borderRadius: '11px',
                background: 'var(--l3)',
                padding: '12px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600 }}>Terms — {I.terms.name}</div>
                <div style={{ flex: 1 }} />
                <button style={ghostBtn} onClick={m.invTermsClose} disabled={I.terms.busy}>
                  Close
                </button>
                <button
                  style={primaryBtn}
                  onClick={m.invTermsSave}
                  disabled={I.terms.busy}
                  aria-label="Save terms"
                >
                  {I.terms.busy ? 'Saving…' : 'Save terms'}
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={fieldLabel} htmlFor="pf-inv-model">
                    Model
                  </label>
                  <select
                    id="pf-inv-model"
                    style={field}
                    value={I.terms.draft.model}
                    onChange={(e) =>
                      m.invTermsSet?.({
                        model: e.target.value as 'profit_split' | 'fixed' | 'shareholding' | 'hybrid',
                      })
                    }
                  >
                    <option value="profit_split">Profit split</option>
                    <option value="fixed">Fixed</option>
                    <option value="shareholding">Shareholding</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
                <div>
                  <label style={fieldLabel} htmlFor="pf-inv-split">
                    Profit split %
                  </label>
                  <input
                    id="pf-inv-split"
                    style={field}
                    inputMode="decimal"
                    value={I.terms.draft.profitSplitPct ?? ''}
                    placeholder="—"
                    onChange={(e) =>
                      m.invTermsSet?.({
                        profitSplitPct: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
              <div>
                <label style={fieldLabel} htmlFor="pf-inv-notes">
                  Notes
                </label>
                <input
                  id="pf-inv-notes"
                  style={field}
                  value={I.terms.draft.notes}
                  placeholder="Anything the split alone does not say"
                  onChange={(e) => m.invTermsSet?.({ notes: e.target.value })}
                />
              </div>
            </div>
          ) : null}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 }}>
          <div style={card}>
            <div style={label}>Grant viewer access</div>
            <div>
              <label style={fieldLabel} htmlFor="pf-inv-name">
                Investor name
              </label>
              <input
                id="pf-inv-name"
                style={field}
                value={I?.form.name ?? ''}
                placeholder="e.g. Halcyon Capital"
                onChange={(e) => m.invSetForm?.('name', e.target.value)}
              />
            </div>
            <div>
              <label style={fieldLabel} htmlFor="pf-inv-email">
                Email
              </label>
              <input
                id="pf-inv-email"
                style={field}
                value={I?.form.email ?? ''}
                placeholder="the link goes to a person"
                onChange={(e) => m.invSetForm?.('email', e.target.value)}
              />
            </div>
            <div>
              <label style={fieldLabel} htmlFor="pf-inv-expires">
                Expires (optional)
              </label>
              <input
                id="pf-inv-expires"
                style={field}
                type="date"
                value={I?.form.expires ?? ''}
                onChange={(e) => m.invSetForm?.('expires', e.target.value)}
              />
            </div>
            {I?.formError ? <div style={{ fontSize: '11px', color: 'var(--neg)' }}>{I.formError}</div> : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                style={primaryBtn}
                onClick={m.invGrantGo}
                disabled={!!I?.busy}
                aria-label="Create the grant"
              >
                {I?.busy ? 'Granting…' : 'Create grant'}
              </button>
            </div>
            {I?.issued ? (
              <div
                style={{
                  border: '1px solid color-mix(in srgb, var(--pos) 35%, transparent)',
                  borderRadius: '11px',
                  background: 'color-mix(in srgb, var(--pos) 6%, transparent)',
                  padding: '11px 13px',
                  fontSize: '11px',
                  color: 'var(--text-mid)',
                  lineHeight: 1.6,
                }}
              >
                {I.issued.token ? (
                  <>
                    <b>{I.issued.name}</b>’s portal link is ready — and this is the only time it exists in
                    plaintext. Copy it now; only its hash survives.
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <button style={primaryBtn} onClick={m.invCopyLink} aria-label="Copy the portal link">
                        Copy link
                      </button>
                      <button style={ghostBtn} onClick={m.invIssuedDismiss}>
                        Done
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <b>{I.issued.name}</b> is granted, but this response carries no link: the token exists in
                    plaintext exactly once, at first issue, and a REPLAYED request cannot honestly re-mint it.
                    If the link is lost, revoke this grant and issue a fresh one.
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <button style={ghostBtn} onClick={m.invIssuedDismiss}>
                        Done
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : null}
          </div>

          <div style={card}>
            <div style={label}>Payments</div>
            {(m.invPayments ?? []).length === 0 ? (
              <div style={{ fontSize: '11.5px', color: 'var(--muted)' }}>No payments recorded.</div>
            ) : (
              (m.invPayments ?? []).map((p) => (
                <div key={p.id} style={rowCard}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '11.5px', fontWeight: 600 }}>{p.grant}</div>
                    <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{p.at}</div>
                  </div>
                  <span style={{ fontSize: '11.5px', fontVariantNumeric: 'tabular-nums' }}>{p.amount}</span>
                  <span
                    style={{
                      fontSize: '10px',
                      padding: '3px 10px',
                      borderRadius: '99px',
                      border: '1px solid var(--l10)',
                      color: p.status === 'paid' ? 'var(--pos)' : 'var(--muted2)',
                      textTransform: 'capitalize',
                    }}
                  >
                    {p.status}
                  </span>
                </div>
              ))
            )}
          </div>

          <div style={card}>
            <div style={label}>Interest</div>
            {(m.invInterests ?? []).map((iv, i) => (
              <div key={i} style={rowCard}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '11.5px', fontWeight: 600 }}>{iv.name}</div>
                  <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{iv.offer}</div>
                </div>
                <span
                  style={{
                    fontSize: '10px',
                    padding: '3px 10px',
                    borderRadius: '99px',
                    color: iv.badgeFg,
                    background: iv.badgeBg,
                  }}
                >
                  {iv.badge}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
